import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { resolveCommerceImageUrl } from '@/lib/media';

// min-height (not a fixed height) on every breakpoint -- a fixed lg: height
// combined with overflow-hidden on the card clipped the CTA button whenever
// content needed more room than the target height (long org name/title, or
// the narrower column the editor's live preview renders it in).
const HEIGHT_CLASSES = {
  compact: 'min-h-[360px] sm:min-h-[320px] lg:min-h-[300px]',
  normal: 'min-h-[400px] sm:min-h-[360px] lg:min-h-[340px]',
  large: 'min-h-[440px] sm:min-h-[400px] lg:min-h-[380px]',
};

// Each layout_variant carries both "has a background photo" and where the
// content column sits -- for 'links'/'rechts' the rest of the photo stays
// visible next to the text (see desktop layout in the theme editor design).
const LAYOUT_VARIANTS = {
  background_image_centered_text: { hasImage: true, align: 'items-center text-center mx-auto' },
  background_image_text_left: { hasImage: true, align: 'items-start text-left' },
  background_image_text_right: { hasImage: true, align: 'items-end text-right ml-auto' },
  minimal_no_image: { hasImage: false, align: 'items-center text-center mx-auto' },
};

const BUTTON_SHAPE_CLASSES = {
  square: 'rounded-none',
  rounded: 'rounded-xl',
  pill: 'rounded-full',
};

const FONT_STACKS = {
  manrope: "'Manrope', sans-serif",
  inter: "'Inter', sans-serif",
  fraunces: "'Fraunces', serif",
};

// section is a commerce_theme ThemeSection (see backend/commerce_theme.py):
// { enabled, layout_variant, settings: { title, subtitle, primary_cta,
// text_color, overlay, height, image } }. Rendering enabled/disabled is the
// caller's responsibility (matches how the section list will be filtered).
//
// theme is the org's global huisstijl (theme.draft/published.settings from
// commerce_theme.py) -- optional, falls back to the original hard-coded teal
// look when omitted so existing callers keep working unchanged.
const Hero = ({ organizationName, onCtaClick, section = {}, theme = {} }) => {
  const { layout_variant: layoutVariant = 'minimal_no_image', settings = {} } = section;
  const {
    title,
    subtitle,
    primary_cta: primaryCta,
    text_color: textColor = '#FFFFFF',
    overlay = {},
    height = 'normal',
    image,
    badge: badgeSettings,
  } = settings;
  const primaryColor = theme.colors?.primary || '#0F766E';
  const accentColor = theme.colors?.accent || '#115E59';
  const buttonShapeClass = BUTTON_SHAPE_CLASSES[theme.buttons?.shape] || BUTTON_SHAPE_CLASSES.rounded;
  const headingFont = FONT_STACKS[theme.typography?.heading_font];
  const bodyFont = FONT_STACKS[theme.typography?.body_font];

  const variant = LAYOUT_VARIANTS[layoutVariant] || LAYOUT_VARIANTS.minimal_no_image;
  const heightClass = HEIGHT_CLASSES[height] || HEIGHT_CLASSES.normal;
  const backgroundImageUrl = variant.hasImage ? resolveCommerceImageUrl(image) : null;
  // Badge defaults to on/auto-derived so existing heroes keep their current
  // look -- badgeSettings only exists once someone has touched the toggle or
  // text in the editor (see design change: this used to be hard-coded).
  const badgeEnabled = badgeSettings?.enabled !== false;
  const badge = badgeSettings?.text || `${organizationName} · shop`.toUpperCase();
  const buttonText = primaryCta?.text;
  const buttonLink = primaryCta?.link;

  const cta = (
    <Button
      size="lg"
      onClick={buttonLink ? undefined : onCtaClick}
      className={`w-full sm:w-auto ${buttonShapeClass} bg-white shadow-lg hover:bg-slate-50`}
      style={{ color: primaryColor }}
    >
      {buttonText || 'Bekijk producten'}
      <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
  );

  return (
    <div
      className={`relative rounded-2xl overflow-hidden text-white flex items-center ${heightClass} ${
        backgroundImageUrl ? 'bg-no-repeat bg-cover bg-right lg:bg-center' : ''
      }`}
      style={backgroundImageUrl
        ? { backgroundImage: `url(${backgroundImageUrl})` }
        : { backgroundImage: `linear-gradient(to bottom right, ${primaryColor}, ${accentColor})` }}
    >
      {backgroundImageUrl && (
        <>
          {/* org-configurable brand tint, kept subtle so the legibility gradient below still reads */}
          <div
            className="absolute inset-0"
            style={{ backgroundColor: overlay.color || '#0F766E', opacity: (overlay.strength ?? 60) / 200 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent lg:bg-gradient-to-r lg:from-black/75 lg:via-black/40 lg:to-transparent" />
        </>
      )}
      <div className="relative z-10 w-full px-6 py-8 sm:px-10 lg:px-12">
        <div className={`flex flex-col gap-3 sm:gap-4 max-w-md lg:max-w-[520px] ${variant.align}`}>
          {badgeEnabled && (
            <span className="inline-flex w-fit items-center text-[11px] sm:text-xs font-semibold tracking-wide bg-white/15 backdrop-blur-sm rounded-full px-3 py-1">
              {badge}
            </span>
          )}
          {/* No fallback title/subtitle when a background photo is set -- the
              photo itself may already carry its own text (logo banners,
              baked-in headlines), so an empty field means "show nothing"
              here, not a generic placeholder on top of it. The gradient-only
              fallback (no photo) still needs *some* text, so it keeps one. */}
          {(title || !backgroundImageUrl) && (
            <h1
              className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold leading-tight"
              style={{ color: textColor, fontFamily: headingFont }}
            >
              {title || `Welkom in de webshop van ${organizationName}`}
            </h1>
          )}
          {(subtitle || !backgroundImageUrl) && (
            <p className="text-sm sm:text-base" style={{ color: textColor, opacity: 0.85, fontFamily: bodyFont }}>
              {subtitle || 'Boeken, merchandise en meer — rechtstreeks van onze gemeente.'}
            </p>
          )}
          {buttonLink ? (
            <a href={buttonLink} className="w-full sm:w-auto pt-1">{cta}</a>
          ) : (
            <div className="pt-1 w-full sm:w-auto">{cta}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Hero;
